import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { toast } from '../../stores/toast.store';
import { confirm as confirmDialog } from '../../stores/confirm.store';
import type { PipelineKind, StageOutcome } from '@masamune/shared';

/**
 * Configurazione delle pipeline — tipi, query e azioni della schermata
 * PipelinesPage.
 *
 * API: /api/v1/pipelines (apps/api/src/modules/deals/pipelines.*). È una rotta
 * diversa da `/deals/pipelines`, che la pagina Trattative continua a usare per
 * riempire tendine e colonne: lì servono solo le pipeline accese, qui servono
 * anche le spente e i conteggi step per step.
 *
 * Ogni azione invalida due famiglie di query: `['pipelines']` (questa schermata)
 * e `['deals']`, perché rinominare una pipeline o aggiungere uno step cambia
 * subito quello che si vede in Trattative.
 */

export interface PipelineStageConfig {
  id: string;
  pipelineId: string;
  name: string;
  order: number;
  outcome: StageOutcome;
  probability: number;
  color: string | null;
  /** Trattative vive ferme su questo step: è il motivo per cui non si può togliere. */
  dealsCount: number;
}

export interface PipelineConfig {
  id: string;
  /** Nome interno, generato dal nome alla nascita e poi immutabile (lo usano i link). */
  slug: string;
  name: string;
  description: string | null;
  kind: PipelineKind;
  order: number;
  active: boolean;
  stages: PipelineStageConfig[];
  dealsCount: number;
}

export const pipelinesConfigQuery = {
  queryKey: ['pipelines', 'config'] as const,
  queryFn: async (): Promise<PipelineConfig[]> =>
    (await api.get<{ items: PipelineConfig[] }>('/pipelines')).items ?? [],
  staleTime: 30_000,
};

/** Un posto su o un posto giù: l'ordine non si scrive a mano da nessuna parte. */
export type MoveDirection = 'su' | 'giu';

// ── Serve alla schermata per dire a parole cosa cambia ─────────────────────

/** Una pipeline senza uno step vinto non può chiudere niente: si avvisa, non si impedisce. */
export function mancanzeStep(p: PipelineConfig): string[] {
  const out: string[] = [];
  if (!p.stages.some((s) => s.outcome === 'VINTO')) out.push('uno step che vale "vinta"');
  if (!p.stages.some((s) => s.outcome === 'PERSO')) out.push('uno step che vale "persa"');
  return out;
}

export function usePipelineActions() {
  const qc = useQueryClient();
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['pipelines'] });
    // Le tendine e le colonne di Trattative leggono la stessa configurazione
    qc.invalidateQueries({ queryKey: ['deals'] });
  };

  const createPipeline = useMutation({
    mutationFn: (body: { name: string; description?: string | null; kind: PipelineKind }) =>
      api.post<PipelineConfig>('/pipelines', body),
    onSuccess: (p) => { invalidate(); toast.ok('Pipeline creata', `${p.name} · con quattro step di partenza da sistemare`); },
    onError: (e: any) => toast.alert('Pipeline non creata', e?.message ?? 'Riprova'),
  });

  /** PATCH parziale: la schermata salva un campo alla volta, al cambio. */
  const updatePipeline = useMutation({
    mutationFn: ({ id, ...body }: { id: string; name?: string; description?: string | null; kind?: PipelineKind; active?: boolean }) =>
      api.patch<PipelineConfig>(`/pipelines/${id}`, body),
    onSuccess: invalidate,
    onError: (e: any) => toast.alert('Modifica non salvata', e?.message ?? 'Riprova'),
  });

  const movePipeline = useMutation({
    mutationFn: ({ id, direction }: { id: string; direction: MoveDirection }) =>
      api.post(`/pipelines/${id}/move`, { direction }),
    onSuccess: invalidate,
    onError: (e: any) => toast.alert('Spostamento non riuscito', e?.message ?? 'Riprova'),
  });

  /** Elimina con conferma. Ritorna null se l'utente annulla. */
  const deletePipeline = useMutation({
    mutationFn: async ({ id, name }: { id: string; name: string }) => {
      const ok = await confirmDialog({
        title: 'Eliminare questa pipeline?',
        body: `"${name}" sparisce insieme ai suoi step. Si può fare solo se non ci sono trattative dentro.`,
        danger: true,
        confirmLabel: 'Elimina',
        icon: 'trash',
      });
      if (!ok) return null;
      await api.delete(`/pipelines/${id}`);
      return true;
    },
    onSuccess: (done, vars) => { if (done) { invalidate(); toast.ok('Pipeline eliminata', vars.name); } },
    onError: (e: any) => toast.alert('Eliminazione non riuscita', e?.message ?? 'Riprova'),
  });

  const addStage = useMutation({
    mutationFn: ({ pipelineId, ...body }: { pipelineId: string; name: string; probability?: number; outcome?: StageOutcome }) =>
      api.post<PipelineStageConfig>(`/pipelines/${pipelineId}/stages`, body),
    onSuccess: (s) => { invalidate(); toast.ok('Step aggiunto', `${s.name} · in fondo alla pipeline`); },
    onError: (e: any) => toast.alert('Step non aggiunto', e?.message ?? 'Riprova'),
  });

  const updateStage = useMutation({
    mutationFn: ({ stageId, ...body }: { stageId: string; name?: string; probability?: number; outcome?: StageOutcome }) =>
      api.patch<PipelineStageConfig>(`/pipelines/stages/${stageId}`, body),
    onSuccess: invalidate,
    onError: (e: any) => toast.alert('Modifica non salvata', e?.message ?? 'Riprova'),
  });

  const moveStage = useMutation({
    mutationFn: ({ stageId, direction }: { stageId: string; direction: MoveDirection }) =>
      api.post(`/pipelines/stages/${stageId}/move`, { direction }),
    onSuccess: invalidate,
    onError: (e: any) => toast.alert('Spostamento non riuscito', e?.message ?? 'Riprova'),
  });

  const deleteStage = useMutation({
    mutationFn: async ({ stageId, name }: { stageId: string; name: string }) => {
      const ok = await confirmDialog({
        title: 'Eliminare questo step?',
        body: `"${name}" sparisce dalla pipeline. Si può fare solo se non ci sono trattative ferme lì.`,
        danger: true,
        confirmLabel: 'Elimina',
        icon: 'trash',
      });
      if (!ok) return null;
      await api.delete(`/pipelines/stages/${stageId}`);
      return true;
    },
    onSuccess: (done, vars) => { if (done) { invalidate(); toast.ok('Step eliminato', vars.name); } },
    onError: (e: any) => toast.alert('Eliminazione non riuscita', e?.message ?? 'Riprova'),
  });

  return { createPipeline, updatePipeline, movePipeline, deletePipeline, addStage, updateStage, moveStage, deleteStage };
}
